import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Kpi, money } from "../../components/ReportKit";

const ROLES = ["", "couple", "planner", "vendor", "therapist", "admin"];

function ProfileDetail({ id, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); if (id) api.get(`/admin/profiles/${id}`).then((r) => setD(r.data)); }, [id]);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl" data-testid="admin-profile-detail">
        <DialogHeader><DialogTitle className="font-serif font-light text-3xl">{d?.user.name || "Profile"}</DialogTitle></DialogHeader>
        {!d ? <div className="h-32 rounded-2xl bg-[var(--bg)] animate-pulse" /> : (
          <div className="space-y-4 text-sm">
            <p className="text-[var(--muted)]">{d.user.email} · <span className="capitalize">{d.user.role}</span> · plan <b>{d.user.plan || "free"}</b> · joined {d.user.created_at?.slice(0, 10)}</p>
            <div className="flex flex-wrap gap-2">
              {d.wedding && <a href={`/site/${d.wedding.slug}`} target="_blank" rel="noreferrer" className="btn-outline-gold !py-2 text-[11px]"><ExternalLink className="w-3.5 h-3.5" /> Wedding site · {d.guest_count} guests</a>}
              {d.community && <Link to={`/community/u/${d.community.handle}`} className="btn-outline-gold !py-2 text-[11px]">Community profile</Link>}
              {d.vendor && <span className="btn-outline-gold !py-2 text-[11px]">Vendor: {d.vendor.business_name} ({d.vendor.status})</span>}
            </div>
            {d.report && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Kpi label="Leads" value={d.report.leads.new_clients + d.report.leads.new_conversations} />
                <Kpi label="Bookings" value={d.report.bookings.booked + d.report.bookings.completed} />
                <Kpi label="Received" value={money(d.report.revenue.received)} />
                <Kpi label="Views" value={d.report.audience.profile_views} />
              </div>
            )}
            <div>
              <p className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] mb-2">Contacts ({d.contacts.length})</p>
              <div className="max-h-56 overflow-y-auto divide-y divide-[var(--line)] border border-[var(--line)] rounded-xl" data-testid="admin-profile-contacts">
                {d.contacts.length === 0 && <p className="p-3 text-[var(--muted)]">No contacts.</p>}
                {d.contacts.map((c) => <p key={c.contact_id} className="px-3 py-2 flex justify-between gap-2"><span className="truncate">{c.name} <span className="text-[var(--muted)]">{c.email}</span></span><span className="text-[11px] uppercase text-[var(--muted)]">{c.kind}{c.status && ` · ${c.status}`}</span></p>)}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminProfilesPage() {
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get(`/admin/profiles?${new URLSearchParams(Object.entries({ role, q }).filter(([, v]) => v))}`).then((r) => setRows(r.data)).catch(() => setRows([])), 200);
    return () => clearTimeout(t);
  }, [role, q]);

  return (
    <div className="max-w-6xl space-y-5" data-testid="admin-profiles-page">
      <div>
        <p className="font-script text-[var(--gold)] text-3xl">Everyone</p>
        <h1 className="font-serif font-light text-4xl">All profiles</h1>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 justify-between">
        <div className="flex gap-1 overflow-x-auto no-scrollbar">{ROLES.map((r) => <button key={r || "all"} onClick={() => setRole(r)} data-testid={`profiles-role-${r || "all"}`} className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.14em] whitespace-nowrap ${role === r ? "bg-[var(--ink)] text-white" : "text-[var(--muted)]"}`}>{r || "All"}</button>)}</div>
        <label className="flex items-center gap-2 bg-white border border-[var(--line)] rounded-full px-4"><Search className="w-4 h-4 text-[var(--muted)]" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or email" data-testid="profiles-search" className="py-2.5 text-sm outline-none bg-transparent" /></label>
      </div>
      <div className="bg-white border border-[var(--line)] rounded-2xl divide-y divide-[var(--line)]" data-testid="profiles-list">
        {rows === null && <div className="h-20 animate-pulse" />}
        {rows?.length === 0 && <p className="p-6 text-center text-sm text-[var(--muted)]">No profiles match.</p>}
        {rows?.map((u) => (
          <button key={u.user_id} onClick={() => setOpen(u.user_id)} data-testid={`profile-row-${u.user_id}`} className="w-full text-left px-4 py-3 grid grid-cols-[1fr_auto] md:grid-cols-[1.5fr_1fr_100px_90px_90px] gap-3 items-center hover:bg-[var(--bg)] transition-colors">
            <span className="min-w-0"><span className="block truncate font-medium">{u.name || "—"}</span><span className="block truncate text-xs text-[var(--muted)]">{u.email}</span></span>
            <span className="text-[11px] uppercase tracking-[0.14em] text-[#8a6d35]">{u.role}</span>
            <span className="hidden md:block text-sm">{u.plan || "free"}</span>
            <span className="hidden md:block text-sm">{u.contacts} contacts</span>
            <span className="hidden md:block text-xs text-[var(--muted)]">{u.created_at?.slice(0, 10)}</span>
          </button>
        ))}
      </div>
      <ProfileDetail id={open} onClose={() => setOpen(null)} />
    </div>
  );
}
